import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface ClientTeamMember {
  id: string
  client_id: string
  name: string
  email?: string
  phone?: string
  linkedin_url?: string
  title?: string
  tags: string[]
  is_primary_contact: boolean
  is_billing_contact: boolean
  status: 'active' | 'inactive'
  created_at: string
  updated_at: string
}

export interface CreateClientTeamMemberData {
  client_id: string
  name: string
  email?: string
  phone?: string
  linkedin_url?: string
  title?: string
  tags?: string[]
  is_primary_contact?: boolean
  is_billing_contact?: boolean
  status?: 'active' | 'inactive'
}

export function useClientTeamMembers(clientId: string) {
  const queryClient = useQueryClient()
  
  const queryKey = ['client-team-members', clientId]

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<ClientTeamMember[]> => {
      const { data, error } = await supabase
        .from('client_team_members')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching client team members:', error)
        throw error
      }

      return (data || []).map(member => ({
        ...member,
        status: member.status as 'active' | 'inactive'
      }))
    },
    enabled: !!clientId,
  })

  const createMember = useMutation({
    mutationFn: async (memberData: CreateClientTeamMemberData) => {
      const { data, error } = await supabase
        .from('client_team_members')
        .insert([memberData])
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const updateMember = useMutation({
    mutationFn: async ({ id, ...updateData }: Partial<ClientTeamMember> & { id: string }) => {
      const { data, error } = await supabase
        .from('client_team_members')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const deleteMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('client_team_members')
        .delete()
        .eq('id', memberId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    teamMembers: query.data || [],
    loading: query.isLoading,
    error: query.error,
    createMember: createMember.mutateAsync,
    updateMember: updateMember.mutateAsync,
    deleteMember: deleteMember.mutateAsync,
    isCreating: createMember.isPending,
    isUpdating: updateMember.isPending,
    isDeleting: deleteMember.isPending,
  }
}